import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * 🔴 ONE PLACE WHERE THE PAGE SPEAKS — design-system plan DS-6a (5.1; F382, F243, F36, WCAG 4.1.3).
 *
 * No app had an `aria-live` region, so a saved form, a refused posting or a list that finished loading changed the
 * screen in silence for anyone who does not see it. Each shell mounts one `Announcer`; anything inside it calls
 * `useAnnounce()` and says a sentence, which a screen reader reads when it is next idle ("polite"). The region is in
 * the page from the start — a live region inserted together with its text is not reliably read. Sentences said in
 * the same moment are queued and read together, never dropped (DS-6a review: the first of two banners was lost); the
 * same sentence said again is read again (the region is cleared first); and the region empties itself a few seconds
 * later, so a reader who reaches it at the end of the page does not find a stale "Still loading". `announce` returns a
 * cancel function: a skeleton that is gone before its sentence is said takes it back. Outside an `Announcer` (a spec,
 * the showcase) the hook is a no-op, never an error.
 *
 * A refusal that must interrupt (`Banner … announce` with `tone="danger"`, an `ErrorState`) is `role="alert"` on its
 * own element instead.
 */
type Cancel = () => void;
type Announce = (text: string) => Cancel;

const AnnounceContext = createContext<Announce>(() => () => undefined);

/** How long a sentence stays in the region after it is said. */
export const ANNOUNCE_CLEAR_MS = 5000;

export function Announcer({ children }: { children: ReactNode }) {
  const [said, setSaid] = useState('');
  const queue = useRef<Array<{ text: string }>>([]);
  const flush = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clear = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const announce = useCallback<Announce>((text) => {
    const item = { text };
    queue.current.push(item);
    if (flush.current === undefined) {
      setSaid('');
      // Next frame, so a repeated sentence is a change the screen reader hears; everything said until then goes too.
      flush.current = setTimeout(() => {
        flush.current = undefined;
        const out = queue.current.map((q) => q.text).join(' ');
        queue.current = [];
        if (!out) return;
        setSaid(out);
        clearTimeout(clear.current);
        clear.current = setTimeout(() => setSaid(''), ANNOUNCE_CLEAR_MS);
      }, 50);
    }
    return () => {
      queue.current = queue.current.filter((q) => q !== item);
    };
  }, []);
  useEffect(
    () => () => {
      clearTimeout(flush.current);
      clearTimeout(clear.current);
    },
    [],
  );
  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <div className="wb-sr-only" role="status" aria-live="polite" aria-atomic="true">
        {said}
      </div>
    </AnnounceContext.Provider>
  );
}

/** Say a sentence in the page's live region; the function returned takes it back if it has not been said yet. */
export function useAnnounce(): Announce {
  return useContext(AnnounceContext);
}
